<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewDeductions' }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to Deductions">
        Deductions
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Create Deduction</p>
    </div>

    <div class="d-flex justify-content-center mb-5">

      <div class="form-outer">
        <form @submit.prevent="handleSubmit">
          <div class="crud-form position-relative">

            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center">
                <h1 class="mb-0">Create Deduction</h1>
              </div>

              <div class="deduction-content-area position-relative">
                <div class="form-body mt-3 mb-3">

                  <div class="form-row">
                    <div class="form-row-label">
                      <label for="dedName" class="form-label">Name</label>
                    </div>
                    <div class="form-row-input">
                      <input id="dedName" type="text" class="form-control" v-model="form.name"
                        placeholder="Enter deduction name" required>
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label for="dedDescription" class="form-label">Description</label>
                    </div>
                    <div class="form-row-input">
                      <textarea id="dedDescription" class="form-control" rows="3" v-model="form.description"
                        placeholder="Enter description"></textarea>
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label for="dedMode" class="form-label">Calculation Mode</label>
                    </div>
                    <div class="form-row-input">
                      <select class="form-select" id="dedMode" v-model.number="form.mode">
                        <option :value="0">Fixed Amount</option>
                        <option :value="1">Percentage</option>
                      </select>
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label for="dedDefaultValue" class="form-label">
                        Default Value
                      </label>
                    </div>
                    <div class="form-row-input">
                      <input type="number" class="form-control" id="dedDefaultValue"
                        v-model.number="form.defaultValue" min="0" :max="form.mode === 1 ? 100 : undefined"
                        step="0.01" required>
                    </div>
                  </div>

                  <div class="form-row checkbox-row">
                    <div class="form-row-label">
                      <label for="dedFedInd" class="form-label">Federal Deduction</label>
                    </div>
                    <div class="form-row-input">
                      <div class="form-check">
                        <input type="checkbox" class="form-check-input" id="dedFedInd" v-model="form.fedInd"
                          :true-value="1" :false-value="0">
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <div class="action-buttons-container">
              <div class="action-buttons-wrapper">
                <div class="action-button">
                  <button type="submit" class="btn btn-primary edit-btn" :disabled="isLoading">
                    <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>
                    {{ isLoading ? 'Submitting...' : 'Create Deduction' }}
                  </button>
                </div>
              </div>
            </div>

          </div>
        </form>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Swal from 'sweetalert2'
import { useUserStore } from '@/stores/user'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const router = useRouter()
const userStore = useUserStore()

const isLoading = ref(false)

const form = reactive({
  name: '',
  description: '',
  mode: 0,
  fedInd: 0,
  defaultValue: 0,
  activeInd: 1,
})

const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

const handleSubmit = async () => {
  const token = Cookies.get('auth_token')
  isLoading.value = true

  const payload = {
    name: form.name.trim(),
    description: form.description.trim(),
    mode: Number(form.mode),
    fedInd: Number(form.fedInd),
    defaultValue: Number(form.defaultValue),
    activeInd: Number(form.activeInd),
  }

  try {
    const result = await axios.post(`${root_api_url}/payment/deduction-type`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'user-id': userStore.uuid || '',
      },
    })

    if (result.data?.errorMessage) {
      Swal.fire({
        html: `${iconHtmlError}<h1 style="font-weight:600;">Error</h1><p>${result.data.errorMessage}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
      return
    }

    await Swal.fire({
      html: `${iconHtmlSuccess}<h1 style="font-weight:600;">Success</h1><p>Deduction type created successfully.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    router.push({ name: 'ViewDeductions' })
  } catch (error) {
    Swal.fire({
      html: `${iconHtmlError}<h1 style="font-weight:600;">Error</h1><p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to create deduction type.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  if (!userStore.profile) {
    await userStore.fetchProfile()
  }
  document.title = 'Create Deduction'
})
</script>

<style scoped>
.deduction-content-area {
  min-height: 280px;
}

/* Form Container */
.form-outer {
  width: 100%;
  max-width: 800px;
}

.crud-form {
  background: white;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-header h1 {
  font-size: 1.5rem;
  color: #333;
}

/* Form Row Layout */
.form-row {
  display: flex;
  margin-bottom: 1.5rem;
  gap: 1rem;
  align-items: flex-start;
}

.form-row-label {
  flex: 0 0 180px;
  text-align: right;
  padding-top: 0.5rem;
}

.form-row-label label {
  font-weight: 600;
    color: var(--color);
  margin-bottom: 0;
  white-space: nowrap;
}

.form-row-input {
  flex: 1;
}

.checkbox-row {
  align-items: center;
}

.checkbox-row .form-row-label {
  padding-top: 0;
}

/* Action Buttons Container */
.action-buttons-container {
  padding: 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--bg);
}

.action-buttons-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.action-button {
  flex: 0 0 auto;
}

.action-button button {
  min-width: 140px;
  white-space: nowrap;
}

.btn {
  padding: 0.5rem 1rem !important;
  border-radius: 4px !important;
  font-weight: 500 !important;
  transition: all 0.3s ease !important;
}

.edit-btn {
  background-color: #007bff !important;
  border-color: #007bff !important;
}

.edit-btn:hover {
  background-color: #0056b3 !important;
}

/* Tablet Responsiveness (768px - 1024px) */
@media screen and (max-width: 1024px) {
  .form-outer {
    max-width: 100%;
  }

  .form-row-label {
    flex: 0 0 140px;
  }
}

/* Mobile Responsiveness (below 768px) */
@media screen and (max-width: 768px) {
  .form-header h1 {
    font-size: 1.25rem;
  }

  .form-row {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e9ecef;
  }

  .form-row-label {
    flex: none;
    text-align: left;
    padding-top: 0;
  }

  .form-row-label label {
    font-size: 0.875rem;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .form-row-input {
    flex: none;
    width: 100%;
  }

  .action-buttons-container {
    padding: 1rem;
  }

  .action-buttons-wrapper {
    flex-direction: column;
    gap: 0.75rem;
  }

  .action-button {
    width: 100%;
  }

  .action-button button {
    width: 100%;
    min-width: 100%;
  }

  .page-identifier {
    font-size: 0.875rem;
  }

  .page-identifier .material-symbols-outlined {
    font-size: 1.25rem;
  }
}

/* Small Mobile (below 480px) */
@media screen and (max-width: 480px) {
  .form-header h1 {
    font-size: 1.125rem;
  }

  .form-row {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
  }

  .form-row-label label {
    font-size: 0.8125rem;
  }

  .action-buttons-container {
    padding: 0.75rem;
  }

  .btn {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }
}

/* Print Styles */
@media print {

  .action-buttons-container,
  .page-identifier {
    display: none !important;
  }

  .form-outer {
    max-width: 100%;
    box-shadow: none;
  }

  .form-row {
    page-break-inside: avoid;
  }
}
</style>
